/**
 * @file Button.tsx
 * @description Primary button with disabled + loading states. Tailwind
 *   styling, RTL/LTR-neutral via logical properties.
 * @module Components
 * @security No HTML escapes here — children may be text or i18n strings,
 *   never user-supplied HTML.
 */

import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  loading,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps): JSX.Element {
  const isDisabled = disabled === true || loading === true;
  // Default brand styling lives in this string. `className` from callers
  // can OVERRIDE the bg-/hover-/text- classes (e.g., red-700 on a Delete
  // button) because Tailwind keeps the last conflicting class in the
  // emitted CSS — the caller's class lands AFTER ours in the joined
  // string, so it wins.
  return (
    <button
      {...rest}
      disabled={isDisabled}
      className={[
        'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg',
        'bg-brand text-brand-fg text-sm font-medium shadow-sm',
        'hover:bg-brand-strong hover:shadow-lift active:scale-[0.98]',
        'transition-[background-color,box-shadow,transform] duration-150',
        'disabled:bg-slate-300 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
        className ?? '',
      ].join(' ')}
    >
      {loading === true ? <Spinner /> : null}
      {children}
    </button>
  );
}

/** 14px inline spinner — replaces the old "…" text so async buttons read
 *  as busy, not broken. Honors prefers-reduced-motion via globals.css. */
function Spinner(): JSX.Element {
  return (
    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="4" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}
